import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../api/apiClient';
import HoldingChart from '../features/portfolio/components/HoldingChart';

/**
 * Forward-fill missing calendar days (weekends / holidays) with the last
 * known closing price. Only applied for daily-bar ranges (not intraday).
 */
function fillWeekendGaps(points) {
  if (!points || points.length < 2) return points;

  const filled = [];
  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    filled.push(curr);

    if (i < points.length - 1) {
      const currDate = new Date(curr.date || curr.Date || '');
      const nextDate = new Date(points[i + 1].date || points[i + 1].Date || '');
      if (isNaN(currDate) || isNaN(nextDate)) continue;

      // Fill every missing calendar day between curr and next
      const cursor = new Date(currDate);
      cursor.setDate(cursor.getDate() + 1);
      while (cursor < nextDate) {
        const iso = cursor.toISOString().split('T')[0];
        filled.push({ ...curr, date: iso, Date: iso });
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  }
  return filled;
}

// Ranges that use daily bars (gap-fill applies)
const DAILY_BAR_RANGES = new Set(['7d', '30d', '90d', '180d', 'ytd', '1y', '3y', '5y', 'all']);

/**
 * Standalone instrument detail page — used by both /stocks/:ticker and /crypto/:ticker.
 * Shows current price, daily change, and a price chart.
 * Admin-only while Stocks/Crypto workspaces are in development.
 */
export default function InstrumentDetailPage({ assetType = 'Stock' }) {
  const { ticker } = useParams();

  const [quote, setQuote]               = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteError, setQuoteError]     = useState('');

  const [rawPoints, setRawPoints]       = useState([]);
  const [chartRange, setChartRange]     = useState(assetType === 'Crypto' ? '7d' : '1y');
  const [chartLoading, setChartLoading] = useState(false);
  const [fundamentals, setFundamentals] = useState(null);

  /* ── Quote (current price + name) ── */
  useEffect(() => {
    if (!ticker) return;
    setQuoteLoading(true);
    setQuoteError('');
    apiFetch(`/api/stock/quote/${encodeURIComponent(ticker.toUpperCase())}`)
      .then(r => r.json())
      .then(data => {
        if (data?.ticker) setQuote(data);
        else setQuoteError('Instrument not found.');
      })
      .catch(() => setQuoteError('Failed to load quote.'))
      .finally(() => setQuoteLoading(false));
  }, [ticker]);

  /* ── Fundamentals (best-effort, doesn't block) ── */
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const isCryptoTicker = assetType === 'Crypto';
    const url = isCryptoTicker
      ? `/api/crypto/detail/${encodeURIComponent(ticker.toUpperCase())}`
      : `/api/stock/fundamentals/${encodeURIComponent(ticker.toUpperCase())}`;
    apiFetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.available) setFundamentals(data);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [ticker, assetType]);

  /* ── Chart data ── */
  const fetchChart = useCallback(async (range) => {
    if (!ticker) return;
    setChartLoading(true);
    // When Yahoo returns no data for the requested range (e.g. a newly listed
    // token), automatically retry with progressively shorter ranges so the user
    // still sees *something* without having to click manually.
    const fallbackChain = { '1y': '90d', '90d': '30d', '30d': '7d', '7d': '1d' };
    let current = range;
    const tried = new Set();
    try {
      while (current) {
        tried.add(current);
        const res = await apiFetch(
          `/api/stock/history/${encodeURIComponent(ticker.toUpperCase())}?range=${current}`
        );
        if (!res.ok) { setRawPoints([]); return; }
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setRawPoints(data);
          // Sync the range selector when we silently fell back
          if (current !== range) setChartRange(current);
          return;
        }
        const next = fallbackChain[current];
        current = (next && !tried.has(next)) ? next : null;
      }
      setRawPoints([]);
    } catch {
      setRawPoints([]);
    } finally {
      setChartLoading(false);
    }
  }, [ticker]);

  useEffect(() => { fetchChart(chartRange); }, [fetchChart, chartRange]);

  /* ── Save to "recently viewed" in localStorage ── */
  useEffect(() => {
    if (!ticker) return;
    try {
      const key = 'inverow.recentlyViewed';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const upper = ticker.toUpperCase();
      const filtered = existing.filter(e => e.ticker !== upper);
      filtered.unshift({ ticker: upper, assetType, viewedAt: Date.now() });
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 10)));
    } catch { /* localStorage may be unavailable */ }
  }, [ticker, assetType]);

  const handleRangeChange = (r) => { setChartRange(r); fetchChart(r); };

  /* ── Gap-fill weekends for daily-bar ranges ── */
  const chartPoints = useMemo(() => {
    if (DAILY_BAR_RANGES.has(chartRange)) return fillWeekendGaps(rawPoints);
    return rawPoints;
  }, [rawPoints, chartRange]);

  /* ── Daily change from quote endpoint (more accurate than chart diff) ── */
  const dailyChange = (() => {
    if (!rawPoints || rawPoints.length < 2) return null;
    const last = rawPoints[rawPoints.length - 1]?.close ?? rawPoints[rawPoints.length - 1]?.Close;
    const prev = rawPoints[rawPoints.length - 2]?.close ?? rawPoints[rawPoints.length - 2]?.Close;
    if (!last || !prev || prev === 0) return null;
    const abs = last - prev;
    const pct = (abs / prev) * 100;
    return { abs, pct };
  })();

  const isCrypto = assetType === 'Crypto';
  const color    = isCrypto ? '#f59e0b' : 'var(--accent)';

  if (quoteLoading) {
    return (
      <div style={s.page}>
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <span style={s.loadingText}>Loading {ticker?.toUpperCase()}…</span>
        </div>
      </div>
    );
  }

  if (quoteError) {
    return (
      <div style={s.page}>
        <div style={{ color: '#f87171', padding: '40px', textAlign: 'center' }}>{quoteError}</div>
      </div>
    );
  }

  const displayName = quote?.shortName || ticker?.toUpperCase();
  const price       = quote?.currentPrice ?? 0;
  const currency    = quote?.currency ?? 'USD';

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.titleRow}>
          <div>
            <div style={s.tickerBadge(color)}>{ticker?.toUpperCase()}</div>
            <h1 style={s.name}>{displayName}</h1>
          </div>
          <div style={s.priceBlock}>
            <div style={s.price}>
              {price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: price >= 1000 ? 2 : 4 })}
              <span style={s.currency}> {currency}</span>
            </div>
            {dailyChange && (
              <div style={{ ...s.change, color: dailyChange.abs >= 0 ? '#22c55e' : '#f87171' }}>
                {dailyChange.abs >= 0 ? '+' : ''}
                {dailyChange.abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                {' '}({dailyChange.abs >= 0 ? '+' : ''}{dailyChange.pct.toFixed(2)}%)
              </div>
            )}
          </div>
        </div>
        <div style={s.typeBadge(isCrypto)}>
          {isCrypto ? 'Crypto' : 'Stock / ETF'}
        </div>
      </div>

      {/* ── Chart (header hidden — ticker/price already shown above) ── */}
      <div style={s.chartWrap}>
        <HoldingChart
          ticker={ticker?.toUpperCase()}
          displayName={displayName}
          loading={chartLoading}
          points={chartPoints}
          range={chartRange}
          onChangeRange={handleRangeChange}
          instrumentCurrency={currency}
          transactions={[]}
          hideHeader
        />
      </div>

      {/* ── Statistics section: heading + KeyStats panel + Statistics cards ── */}
      <h2 style={s.statsHeading}>Statistics</h2>
      <KeyStats quote={quote} currency={currency} />
      {fundamentals && (
        isCrypto
          ? <CryptoStatistics data={fundamentals} currency={currency} />
          : <Statistics data={fundamentals} currency={currency} />
      )}

      {/* ── Dev notice ── */}
      <div style={s.devNotice}>
        🚧 Full {isCrypto ? 'Crypto' : 'Stocks'} workspace is in development — more data coming soon.
      </div>
    </div>
  );
}

function CryptoStatistics({ data, currency }) {
  const fmtMoney = (v) => {
    if (v == null) return '—';
    const abs = Math.abs(v);
    if (abs >= 1e12) return (v / 1e12).toFixed(2) + 'T ' + currency;
    if (abs >= 1e9)  return (v / 1e9).toFixed(2)  + 'B ' + currency;
    if (abs >= 1e6)  return (v / 1e6).toFixed(2)  + 'M ' + currency;
    if (abs >= 1e3)  return (v / 1e3).toFixed(2)  + 'K ' + currency;
    return v.toFixed(2) + ' ' + currency;
  };
  const fmtNum = (v) => v == null ? '—' :
    Math.abs(v) >= 1e9 ? (v / 1e9).toFixed(2) + 'B' :
    Math.abs(v) >= 1e6 ? (v / 1e6).toFixed(2) + 'M' :
    Math.abs(v) >= 1e3 ? (v / 1e3).toFixed(2) + 'K' :
    v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const fmtPct = (v) => v == null ? '—' :
    (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
  const fmtPrice = (v) => v == null ? '—' :
    v >= 1 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + ' ' + currency
           : v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 8 }) + ' ' + currency;
  const colorOf = (v) => v == null ? undefined : v >= 0 ? '#22c55e' : '#f87171';

  const sections = [
    {
      title: 'Market',
      items: [
        { label: 'Market Cap',          value: fmtMoney(data.marketCap) },
        { label: 'Fully Diluted Cap',   value: fmtMoney(data.fullyDilutedCap) },
        { label: '24h Volume',          value: fmtMoney(data.totalVolume) },
        { label: 'Market Cap Rank',     value: data.marketCapRank ? '#' + data.marketCapRank : '—' },
      ],
    },
    {
      title: "24-hour range",
      items: [
        { label: '24h High',            value: fmtPrice(data.high24h) },
        { label: '24h Low',             value: fmtPrice(data.low24h) },
        { label: '24h Change',          value: fmtPct(data.priceChange24h), color: colorOf(data.priceChange24h) },
        { label: '7d Change',           value: fmtPct(data.priceChange7d),  color: colorOf(data.priceChange7d) },
        { label: '30d Change',          value: fmtPct(data.priceChange30d), color: colorOf(data.priceChange30d) },
        { label: '1y Change',           value: fmtPct(data.priceChange1y),  color: colorOf(data.priceChange1y) },
      ],
    },
    {
      title: 'Supply',
      items: [
        { label: 'Circulating',         value: fmtNum(data.circulatingSupply) },
        { label: 'Total',               value: fmtNum(data.totalSupply) },
        { label: 'Max',                 value: data.maxSupply == null ? '∞' : fmtNum(data.maxSupply) },
      ],
    },
    {
      title: 'All-time records',
      items: [
        { label: 'All-time high',       value: fmtPrice(data.ath) },
        { label: 'From ATH',            value: fmtPct(data.athChangePct), color: colorOf(data.athChangePct) },
        { label: 'All-time low',        value: fmtPrice(data.atl) },
        { label: 'From ATL',            value: fmtPct(data.atlChangePct), color: colorOf(data.atlChangePct) },
      ],
    },
  ];

  return (
    <div style={statisticsStyle.wrap}>
      <div style={statisticsStyle.grid}>
        {sections.map(section => (
          <div key={section.title} style={statisticsStyle.card}>
            <div style={statisticsStyle.sectionTitle}>{section.title}</div>
            <div style={statisticsStyle.rows}>
              {section.items.map(item => (
                <div key={item.label} style={statisticsStyle.row}>
                  <span style={statisticsStyle.rowLabel}>{item.label}</span>
                  <span style={{ ...statisticsStyle.rowValue, ...(item.color ? { color: item.color } : {}) }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Statistics({ data, currency }) {
  const PLACEHOLDER = 'Coming soon';
  const fmtNum = (v, dec = 2) => v == null ? PLACEHOLDER :
    v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const fmtMoney = (v) => {
    if (v == null) return PLACEHOLDER;
    const abs = Math.abs(v);
    if (abs >= 1e12) return (v / 1e12).toFixed(2) + 'T ' + currency;
    if (abs >= 1e9)  return (v / 1e9).toFixed(2)  + 'B ' + currency;
    if (abs >= 1e6)  return (v / 1e6).toFixed(2)  + 'M ' + currency;
    if (abs >= 1e3)  return (v / 1e3).toFixed(2)  + 'K ' + currency;
    return v.toFixed(2) + ' ' + currency;
  };
  const fmtPct = (v) => v == null ? PLACEHOLDER :
    (v < 1 ? (v * 100).toFixed(2) : v.toFixed(2)) + '%';

  const sections = [
    {
      title: 'Overview',
      items: [
        { label: 'Market Cap',          value: fmtMoney(data.marketCap) },
        { label: 'Enterprise Value',    value: fmtMoney(data.enterpriseValue) },
        { label: 'Avg Volume',          value: data.avgVolume != null ? fmtMoney(data.avgVolume).replace(' ' + currency, '') : PLACEHOLDER },
        { label: 'Beta',                value: fmtNum(data.beta) },
        { label: 'EPS (TTM)',           value: data.eps != null ? fmtNum(data.eps) + ' ' + currency : PLACEHOLDER },
        { label: 'Dividend Yield',      value: fmtPct(data.dividendYield) },
      ],
    },
    {
      title: 'Valuation',
      items: [
        { label: 'P/E (TTM)',           value: fmtNum(data.peRatio) },
        { label: 'Forward P/E',         value: fmtNum(data.forwardPe) },
        { label: 'Price / Sales',       value: fmtNum(data.priceToSales) },
        { label: 'Price / Book',        value: fmtNum(data.priceToBook) },
      ],
    },
    {
      title: 'Dividends',
      items: [
        { label: 'Annual / share',      value: data.annualDividendPerShare != null ? fmtNum(data.annualDividendPerShare) + ' ' + currency : PLACEHOLDER },
        { label: 'Payout Ratio',        value: fmtPct(data.payoutRatio) },
      ],
    },
  ];

  // Profile context — only show if at least one field present
  const profile = [
    data.exchange && { label: 'Exchange', value: data.exchange },
    data.sector   && { label: 'Sector',   value: data.sector },
    data.industry && { label: 'Industry', value: data.industry },
    data.country  && { label: 'Country',  value: data.country },
  ].filter(Boolean);
  if (profile.length) sections.push({ title: 'Profile', items: profile });

  return (
    <div style={statisticsStyle.wrap}>
      <div style={statisticsStyle.grid}>
        {sections.map(section => (
          <div key={section.title} style={statisticsStyle.card}>
            <div style={statisticsStyle.sectionTitle}>{section.title}</div>
            <div style={statisticsStyle.rows}>
              {section.items.map(item => {
                const isPlaceholder = item.value === 'Coming soon';
                return (
                  <div key={item.label} style={statisticsStyle.row}>
                    <span style={statisticsStyle.rowLabel}>{item.label}</span>
                    <span style={isPlaceholder ? statisticsStyle.rowValuePlaceholder : statisticsStyle.rowValue}>
                      {item.value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const statisticsStyle = {
  wrap: { marginTop: '24px' },
  title: { margin: '0 0 14px', fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '14px',
  },
  card: {
    background: 'var(--surface-strong)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    padding: '16px 18px',
    boxShadow: 'var(--shadow)',
  },
  sectionTitle: {
    fontSize: '11px', fontWeight: 800,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '10px',
  },
  rows: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' },
  rowLabel: { fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 600 },
  rowValue: { fontSize: '14px', color: 'var(--text-primary)', fontWeight: 700, textAlign: 'right' },
  rowValuePlaceholder: { fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, fontStyle: 'italic', textAlign: 'right', opacity: 0.7 },
};

function KeyStats({ quote, currency }) {
  const fmt = (v) => v == null ? '—' :
    v >= 1000 ? v.toLocaleString('en-US', { maximumFractionDigits: 2 }) :
    v >= 1    ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) :
                v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 });

  // All fields come straight from Yahoo Finance meta via /api/stock/quote
  const items = [
    { label: 'Previous close', value: quote?.previousClose },
    { label: "Day's high",     value: quote?.dayHigh },
    { label: "Day's low",      value: quote?.dayLow },
    { label: '52-week high',   value: quote?.fiftyTwoWeekHigh },
    { label: '52-week low',    value: quote?.fiftyTwoWeekLow },
    { label: 'Currency',       value: currency, raw: true },
  ];

  return (
    <div style={keyStatsStyle.wrap}>
      {items.map(item => (
        <div key={item.label} style={keyStatsStyle.cell}>
          <div style={keyStatsStyle.label}>{item.label}</div>
          <div style={keyStatsStyle.value}>
            {item.raw ? (item.value || '—') : fmt(item.value)}
          </div>
        </div>
      ))}
    </div>
  );
}

const keyStatsStyle = {
  wrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '1px',
    background: 'var(--border)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginTop: '16px',
  },
  cell: {
    background: 'var(--surface-strong)',
    padding: '12px 16px',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  label: {
    fontSize: '11px', fontWeight: 700,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.4px',
  },
  value: {
    fontSize: '16px', fontWeight: 700,
    color: 'var(--text-primary)',
  },
};

/* ── Styles ── */
const s = {
  page: { padding: '0 0 40px' },

  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '80px 0', color: 'var(--text-secondary)' },
  spinner: {
    width: '32px', height: '32px', borderRadius: '50%',
    border: '3px solid var(--border)',
    borderTopColor: 'var(--accent)',
    animation: 'spin 0.7s linear infinite',
  },
  loadingText: { fontSize: '15px' },

  header: { marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' },
  titleRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' },

  tickerBadge: (color) => ({
    display: 'inline-block',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
    color, background: `${color}22`,
    padding: '3px 10px', borderRadius: '6px',
    marginBottom: '6px',
  }),
  name: { margin: 0, fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2 },

  priceBlock: { textAlign: 'right', flexShrink: 0 },
  price: { fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 },
  currency: { fontSize: '16px', fontWeight: 600, opacity: 0.6 },
  change: { fontSize: '14px', fontWeight: 600, marginTop: '4px' },

  typeBadge: (isCrypto) => ({
    display: 'inline-block',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px',
    color: isCrypto ? '#f59e0b' : 'var(--accent)',
    background: isCrypto ? 'rgba(245,158,11,0.10)' : 'rgba(99,102,241,0.10)',
    padding: '3px 10px', borderRadius: '6px',
    alignSelf: 'flex-start',
  }),

  chartWrap: { marginTop: '16px' },

  statsHeading: {
    margin: '32px 0 14px',
    fontSize: '20px',
    fontWeight: 800,
    color: 'var(--text-primary)',
  },

  devNotice: {
    marginTop: '24px',
    padding: '14px 18px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
};
